import React, {useEffect, useRef, useState} from "react";
import {SourceTranslationField} from "./SourceTranslationField";
import {TargetTranslationField} from "./TargetTranslationField";
import {useDispatch, useSelector} from "react-redux";
import {
    postTranslatables,
    setActiveRecord,
    setActiveRecordId,
    setSaveSuccess,
    setSourceDescription,
    setSourceName,
    setSourceWelcomeSlogan,
    setTargetDescription,
    setTargetName,
    setTargetWelcomeSlogan
} from "../../../../../slices/translationsSlice";
import {fetchActiveMenu} from "../../../../../slices/cmsSlice";
import {fetchIngredients} from "../../../../../slices/dishAdditionsSlice";
import {ButtonsWrapper, DialogContainer, ScrollableWrapper} from "./TranslationsEditor.style";
import {LoadingSpinner} from "../../../../icons/LoadingSpinner";
import {useTranslation} from "react-i18next";
import {BorderedButton} from "../../../common/BorderedButton";
import {ActionButton} from "../../../common/ActionButton";
import {useOutsideClick} from "../../../../../hooks/useOutsideClick";

export const TranslationsEditor = ({fetchRecords}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const editorRef = useRef(null);
    const {
        activeRecord,
        targetName,
        targetDescription,
        targetWelcomeSlogan,
        sourceName,
        sourceDescription,
        sourceWelcomeSlogan,
    } = useSelector(state => state.translations.view);
    const isPostLoading = useSelector(state => state.translations.postTranslatables.isLoading);
    const {restaurant} = useSelector(state => state.dashboard.view);
    const {chosenDestinationLanguage} = useSelector(state => state.translations.view);
    const [confirmationTimeoutId, setConfirmationTimeoutId] = useState(null);
    const destinationValue = chosenDestinationLanguage?.value?.toLowerCase();
    const restaurantLanguage = restaurant?.value.settings.language.toLowerCase();
    const [nameBlank, setNameBlank] = useState(false);
    const [descriptionBlank, setDescriptionBlank] = useState(false);
    const [welcomeSloganBlank, setWelcomeSloganBlank] = useState(false);
    const [nameTooLong, setNameTooLong] = useState(false);
    const [descriptionTooLong, setDescriptionTooLong] = useState(false);
    const [welcomeSloganTooLong, setWelcomeSloganTooLong] = useState(false);

    useOutsideClick([editorRef], () => dispatch(setActiveRecord(null)));

    useEffect(() => {
        if (!activeRecord) return;
        dispatch(setSourceName(activeRecord.name?.[restaurantLanguage] ?? ''));
        dispatch(setSourceDescription(activeRecord.description?.[restaurantLanguage] ?? ''));
        dispatch(setSourceWelcomeSlogan(activeRecord.message?.[restaurantLanguage] ?? ''));
        dispatch(setTargetName(activeRecord.name?.[destinationValue] ?? ''));
        dispatch(setTargetDescription(activeRecord.description?.[destinationValue] ?? ''));
        dispatch(setTargetWelcomeSlogan(activeRecord.message?.[destinationValue] ?? ''));
    }, [activeRecord, dispatch, restaurantLanguage, destinationValue]);

    const isInvalid = () => {
        let nextNameBlank = false;
        let nextNameTooLong = false;
        let nextDescriptionBlank = false;
        let nextDescriptionTooLong = false;
        let nextWelcomeSloganBlank = false;
        let nextWelcomeSloganTooLong = false;

        if ('name' in activeRecord && !('message' in activeRecord)) {
            nextNameBlank = targetName.length === 0;
            nextNameTooLong = targetName.length > 100;
            setNameBlank(nextNameBlank);
            setNameTooLong(nextNameTooLong);
        }

        if ('description' in activeRecord && activeRecord?.description?.[restaurantLanguage]) {
            nextDescriptionBlank = targetDescription.length === 0;
            nextDescriptionTooLong = targetDescription.length > 255;
            setDescriptionBlank(nextDescriptionBlank);
            setDescriptionTooLong(nextDescriptionTooLong);
        }

        if ('message' in activeRecord) {
            nextWelcomeSloganBlank = targetWelcomeSlogan.length === 0;
            nextWelcomeSloganTooLong = targetWelcomeSlogan.length > 100;
            setWelcomeSloganBlank(nextWelcomeSloganBlank);
            setWelcomeSloganTooLong(nextWelcomeSloganTooLong);
        }

        return (
            nextNameBlank || nextNameTooLong ||
            nextDescriptionBlank || nextDescriptionTooLong ||
            nextWelcomeSloganBlank || nextWelcomeSloganTooLong
        );
    };

    const discardDialog = () => {
        dispatch(setActiveRecord(null));
        dispatch(setActiveRecordId(null));
    }

    const handleTranslatablesSubmit = async (e) => {
        e.preventDefault();
        if (isInvalid()) return;
        const resultAction = await dispatch(postTranslatables({targetLanguage: destinationValue}));
        if (postTranslatables.fulfilled.match(resultAction)) {
            discardDialog();
            dispatch(setSaveSuccess(true));

            if (confirmationTimeoutId) {
                clearTimeout(confirmationTimeoutId);
            }

            const newConfirmationTimeoutId = setTimeout(() => {
                dispatch(setSaveSuccess(false));
            }, 2000);
            setConfirmationTimeoutId(newConfirmationTimeoutId);

            await dispatch(fetchActiveMenu());
            await dispatch(fetchIngredients());
            await fetchRecords({force: true});
        }
    }

    const renderDescriptionTranslatable = () => {
        const restaurantLanguage = restaurant?.value.settings.language.toLowerCase();
        if (!activeRecord || !('description' in activeRecord) || !activeRecord.description[restaurantLanguage]) return;
        return (
            <>
                <div className={'translation-fields-separator'}/>
                <div className={'translation-fields-wrapper'}>
                    <SourceTranslationField value={sourceDescription}
                                            type={'description'}/>
                    <TargetTranslationField
                        value={targetDescription}
                        changeHandler={setTargetDescription}
                        type={'description'}
                        isTooLong={descriptionTooLong}
                        isBlank={descriptionBlank}
                        resetValidation={() => {
                            setDescriptionTooLong(false);
                            setDescriptionBlank(false);
                        }}/>
                </div>
            </>
        );
    };

    const renderNameTranslatable = () => {
        if (!activeRecord || 'message' in activeRecord) return;
        return (
            <section>
                <form className={'translation-wrapper'}>
                    <div className={'translation-fields-wrapper'}>
                        <SourceTranslationField value={sourceName}
                                                type={'name'}/>
                        <TargetTranslationField value={targetName}
                                                changeHandler={setTargetName}
                                                type={'name'}
                                                isBlank={nameBlank}
                                                isTooLong={nameTooLong}
                                                resetValidation={() => {
                                                    setNameTooLong(false);
                                                    setNameBlank(false);
                                                }}/>
                    </div>
                    {renderDescriptionTranslatable()}
                </form>
            </section>
        );
    }

    const renderWelcomeSloganTranslatable = () => {
        if (!activeRecord || !('message' in activeRecord)) return;
        return (
            <section>
                <form className={'translation-wrapper'}>
                    <SourceTranslationField value={sourceWelcomeSlogan}
                                            type={'welcomeSlogan'}/>
                    <TargetTranslationField value={targetWelcomeSlogan}
                                            changeHandler={setTargetWelcomeSlogan}
                                            type={'welcomeSlogan'}
                                            isTooLong={welcomeSloganTooLong}
                                            isBlank={welcomeSloganBlank}
                                            resetValidation={() => {
                                                setWelcomeSloganTooLong(false);
                                                setWelcomeSloganBlank(false);
                                            }}/>
                </form>
            </section>
        );
    }

    const renderButtons = () => {
        return (
            <div className={'dialog-footer'}>
                <BorderedButton onClick={discardDialog} text={t('cancel')} isBordered={true}/>
                <ActionButton onClick={handleTranslatablesSubmit}
                              text={isPostLoading ? <LoadingSpinner buttonMode={true}/> : t('save')}/>
            </div>
        );
    }

    return (
        <div className={'overlay'}>
            <DialogContainer ref={editorRef}>
                <ScrollableWrapper>
                    {renderNameTranslatable()}
                    {renderWelcomeSloganTranslatable()}
                    <ButtonsWrapper>
                        {renderButtons()}
                    </ButtonsWrapper>
                </ScrollableWrapper>
            </DialogContainer>
        </div>
    );
};